import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';

import { RichTextPreview } from '@/components/editor/rich-text-preview';
import { type FormField, formFieldPresentation } from '@/components/shared/form/form-fields';
import { RadioGroupInput } from '@/components/ui/form/radio-group-input';
import { TextInput } from '@/components/ui/form/text-input';
import { richTextToPlainText } from '@/lib/rich-text';

const OTHER_VALUE = '__other__';

function optionValueSet(field: FormField) {
  return new Set((field.options ?? []).map((option) => option.value));
}

function optionValueSignature(field: FormField) {
  return (field.options ?? []).map((option) => option.value).join('');
}

function singleChoiceState(field: FormField, value: string) {
  const regularOptionValues = optionValueSet(field);
  const isOther = Boolean(value) && !regularOptionValues.has(value);

  return {
    selectedOption: isOther ? OTHER_VALUE : value,
    otherText: isOther ? value : '',
  };
}

export function SingleChoiceField({
  id,
  field,
  value,
  disabled,
  onChange,
  renderOptionLabel = (label) => <RichTextPreview mode="trusted" as="span" value={label} />,
}: {
  id: string;
  field: FormField;
  value: string;
  disabled: boolean;
  renderOptionLabel?: (label: string) => ReactNode;
  onChange: (val: string) => void;
}) {
  const initialState = singleChoiceState(field, value);
  const optionsSignature = optionValueSignature(field);
  const previousOptionsSignature = useRef(optionsSignature);
  const presentation = formFieldPresentation(field);

  const [selectedOption, setSelectedOption] = useState(initialState.selectedOption);
  const [otherText, setOtherText] = useState(initialState.otherText);

  const allOptions = [
    ...(field.options ?? []),
    ...(field.addOther ? [{ label: field.otherLabel ?? 'Other', value: OTHER_VALUE }] : []),
  ];
  const optionsWithDisabled = disabled
    ? allOptions.map((option) => ({ ...option, disabled: true }))
    : allOptions;

  useEffect(() => {
    const optionsChanged = previousOptionsSignature.current !== optionsSignature;
    previousOptionsSignature.current = optionsSignature;

    const localValue = selectedOption === OTHER_VALUE ? otherText : selectedOption;
    if (!optionsChanged && value === localValue) return;

    const nextState = singleChoiceState(field, value);
    setSelectedOption(nextState.selectedOption);
    setOtherText(nextState.otherText);
  }, [field, optionsSignature, otherText, selectedOption, value]);

  const handleOptionChange = (val: string) => {
    setSelectedOption(val);
    if (val === OTHER_VALUE) {
      setOtherText('');
      onChange('');
    } else {
      onChange(val);
    }
  };

  const handleOtherTextChange = (text: string) => {
    setOtherText(text);
    onChange(text);
  };

  return (
    <div className="grid gap-2">
      <RadioGroupInput
        id={id}
        label={field.label}
        value={selectedOption}
        onValueChange={handleOptionChange}
        options={optionsWithDisabled}
        renderOptionLabel={renderOptionLabel}
        isRequired={field.required}
        showAsterisk={presentation.showAsterisk}
        helperText={presentation.helperText}
        helperTextPlacement="afterControl"
        disabled={disabled}
        layout={field.displayInTwoColumns ? 'grid' : 'vertical'}
      />
      {field.addOther && selectedOption === OTHER_VALUE && (
        <TextInput
          id={`${id}-other`}
          aria-label={`${field.label}: ${richTextToPlainText(field.otherLabel) || 'Other'}`}
          value={otherText}
          placeholder={field.otherPlaceholder ?? 'Add your own option'}
          onChange={(e) => handleOtherTextChange(e.target.value)}
          disabled={disabled}
          isRequired
          showAsterisk
          required
        />
      )}
    </div>
  );
}
